/* Contexte global "jobs d'import d'examen actifs" — monté sur rootRoute
   (voir router.tsx), au même niveau que RolesProvider. Généralise le
   polling local de use-bootstrap-job.ts au niveau de l'app entière : il
   continue tant que l'app est montée, peu importe la route affichée, et
   reprend l'état réel depuis le serveur au montage plutôt que de faire
   confiance au client pour se souvenir de quoi que ce soit (voir plan,
   https://html.naia-edu.app/plan-import-examen-arriere-plan §4.1).

   v1 : un seul import actif à la fois côté serveur (index partiel unique
   sur content_authoring_jobs), mais l'état ici reste une LISTE — coût de
   conception nul aujourd'hui, évite une réécriture quand le multi-import
   arrivera (décision Yassine, 30/09). */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { api } from '../lib/api';
import { useAuth } from './use-auth';

export interface ExamImportWarning {
  readonly type: string;
  readonly sectionId?: string;
  readonly note: string;
}

export interface TrackedExamImportJob {
  readonly id: string;
  readonly documentId: string;
  readonly status: 'running' | 'succeeded' | 'failed';
  readonly step: 'generating' | 'saving' | 'done';
  readonly sectionsTotal: number;
  readonly sectionsCreated: number;
  readonly error: string | null;
  readonly failureKind: string | null;
  readonly warnings: readonly ExamImportWarning[] | null;
}

export interface ExamImportNotice {
  readonly documentId: string;
  readonly status: 'succeeded' | 'failed';
  readonly error: string | null;
  readonly failureKind: string | null;
  readonly warnings: readonly ExamImportWarning[] | null;
}

interface RawJobRow {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  step: 'generating' | 'saving' | 'done';
  sections_total: number;
  sections_created: number;
  error: string | null;
  failure_kind: string | null;
  warnings: readonly ExamImportWarning[] | null;
}

interface Entry {
  job: TrackedExamImportJob;
  /** Une UI mountée (la modale d'import) suit déjà ce job elle-même — pas de
   *  notification globale à sa fin, elle l'affiche elle-même. Sans porteur
   *  (navigation, refresh, onglet fermé puis rouvert) : la barre + le toast
   *  globaux en deviennent responsables. */
  claimed: boolean;
}

interface ExamImportJobsState {
  /** v1 : 0 ou 1 élément — la barre de progression n'agrège rien de plus. */
  jobs: readonly TrackedExamImportJob[];
  notice: ExamImportNotice | null;
  dismissNotice: () => void;
  /** Commence (ou reprend) le suivi d'un document — appelé par la modale dès
   *  qu'elle connaît le documentId d'un job qu'elle vient de démarrer. */
  trackJob: (documentId: string, opts?: { claim?: boolean }) => void;
  /** La modale ne suit plus ce document (fermeture, unmount) — s'il tourne
   *  encore, la barre globale en reprend le suivi ; s'il est déjà terminé,
   *  on suppose qu'elle a déjà montré le résultat, pas de notification. */
  releaseJob: (documentId: string) => void;
  getJob: (documentId: string) => TrackedExamImportJob | undefined;
  /** Compteur global — incrémenté chaque fois qu'une session d'examen vient
   *  d'être créée (`exam.createSession.mutate` réussi), quel que soit l'écran
   *  d'origine (liste ou page de revue post-import). Même principe que
   *  `notice` : "Examens sur table" peut être montée sur un autre onglet au
   *  moment de la création, il faut un signal global, pas un callback local
   *  (NAI-218, point 4b). */
  sessionCreatedTick: number;
  notifySessionCreated: () => void;
}

const ExamImportJobsContext = createContext<ExamImportJobsState | null>(null);

const POLL_MS = 2_000;
const hintKey = (uid: string) => `naia.examImport.activeDocument.${uid}`;

const fromRawRow = (documentId: string, row: RawJobRow): TrackedExamImportJob => ({
  id: row.id,
  documentId,
  status: row.status,
  step: row.step,
  sectionsTotal: row.sections_total,
  sectionsCreated: row.sections_created,
  error: row.error,
  failureKind: row.failure_kind,
  warnings: row.warnings,
});

export function ExamImportJobsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const [entries, setEntries] = useState<Map<string, Entry>>(new Map());
  const [notice, setNotice] = useState<ExamImportNotice | null>(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const persistHint = useCallback((id: string, documentId: string | null) => {
    try {
      if (documentId) localStorage.setItem(hintKey(id), documentId);
      else localStorage.removeItem(hintKey(id));
    } catch { /* localStorage indisponible (navigation privée…) : pas de source de vérité, tant pis */ }
  }, []);

  /* Reconstruction au montage — jamais localStorage comme vérité, juste un
     indice pour éviter un flash "rien en cours" avant la réponse serveur. */
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    let hinted: string | null = null;
    try { hinted = localStorage.getItem(hintKey(uid)); } catch { /* ignore */ }
    if (hinted) {
      setEntries((prev) => {
        if (prev.has(hinted!)) return prev;
        const next = new Map(prev);
        next.set(hinted!, {
          claimed: false,
          job: {
            id: 'pending', documentId: hinted!, status: 'running', step: 'generating',
            sectionsTotal: 0, sectionsCreated: 0, error: null, failureKind: null, warnings: null,
          },
        });
        return next;
      });
    }

    (async () => {
      const active = (await api.content.examImport.activeJobs
        .query()
        .catch(() => [] as (RawJobRow & { document_id: string })[])) as (RawJobRow & { document_id: string })[];
      if (cancelled) return;

      if (active.length > 0) {
        setEntries((prev) => {
          const next = new Map(prev);
          for (const row of active) next.set(row.document_id, { claimed: false, job: fromRawRow(row.document_id, row) });
          return next;
        });
        persistHint(uid, active[0]!.document_id);
        return;
      }

      /* Rien en cours côté serveur : si l'indice local pointait vers un job
         qui s'est terminé pendant l'absence, on va chercher son résultat
         final pour notifier directement, plutôt qu'une barre à 100% qui ne
         mène nulle part. */
      if (hinted) {
        const job = await api.content.examImport.status.query({ documentId: hinted }).catch(() => null) as RawJobRow | null;
        setEntries((prev) => {
          const next = new Map(prev);
          next.delete(hinted!);
          return next;
        });
        if (job && job.status !== 'running') {
          setNotice({ documentId: hinted, status: job.status, error: job.error, failureKind: job.failure_kind, warnings: job.warnings });
        }
        persistHint(uid, null);
      }
    })();

    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- persistHint est stable (useCallback sans deps) */
  }, [uid]);

  /* Un seul point de sondage, réutilisé par l'intervalle ci-dessous ET par
     trackJob (poll immédiat à l'ajout — sans quoi une modale qui vient de
     démarrer un job attendrait jusqu'à POLL_MS avant son premier statut,
     contrairement au comportement d'origine de use-bootstrap-job). */
  const pollDocument = useCallback(async (documentId: string) => {
    const row = (await api.content.examImport.status.query({ documentId }).catch(() => null)) as RawJobRow | null;
    if (!row) return;
    const job = fromRawRow(documentId, row);

    /* Le test d'existence ET la décision running / terminal+claimé /
       terminal+non-claimé se font DANS l'updater, sur `prev` — jamais sur
       entriesRef.current, qui n'est resynchronisé qu'au rendu suivant et
       peut être périmé si un setEntries vient d'être programmé dans le même
       tick (trackJob). Le résultat est capturé dans `outcome`, assigné par
       l'updater, pour déclencher les effets de bord (setNotice,
       persistHint) APRÈS setEntries — jamais à l'intérieur de l'updater,
       que React peut réinvoquer plusieurs fois (StrictMode, re-render
       concurrent), ce qui les dédoublerait. */
    const result: { outcome: 'not-tracked' | 'running' | 'claimed-terminal' | 'unclaimed-terminal' } = { outcome: 'not-tracked' };
    flushSync(() => setEntries((prev) => {
      const e = prev.get(documentId);
      if (!e) { result.outcome = 'not-tracked'; return prev; }
      if (job.status === 'running') {
        result.outcome = 'running';
        const next = new Map(prev);
        next.set(documentId, { ...e, job });
        return next;
      }
      if (e.claimed) {
        result.outcome = 'claimed-terminal';
        const next = new Map(prev);
        next.set(documentId, { ...e, job });
        return next;
      }
      result.outcome = 'unclaimed-terminal';
      const next = new Map(prev);
      next.delete(documentId);
      return next;
    }));

    if (result.outcome === 'claimed-terminal') {
      if (uid) persistHint(uid, null);
    } else if (result.outcome === 'unclaimed-terminal' && job.status !== 'running') {
      if (uid) {
        setNotice({ documentId, status: job.status, error: job.error, failureKind: job.failureKind, warnings: job.warnings });
        persistHint(uid, null);
      }
    }
  }, [uid, persistHint]);

  /* Sondage — tant qu'au moins un job est suivi, peu importe la route. */
  useEffect(() => {
    if (entries.size === 0) return;
    const timer = window.setInterval(() => {
      for (const [documentId] of entriesRef.current) void pollDocument(documentId);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [entries.size, pollDocument]);

  const trackJob = useCallback((documentId: string, opts?: { claim?: boolean }) => {
    setEntries((prev) => {
      const existing = prev.get(documentId);
      const next = new Map(prev);
      next.set(documentId, {
        claimed: opts?.claim ?? existing?.claimed ?? false,
        job: existing?.job?.status === 'running' ? existing.job : {
          id: 'pending', documentId, status: 'running', step: 'generating',
          sectionsTotal: 0, sectionsCreated: 0, error: null, failureKind: null, warnings: null,
        },
      });
      return next;
    });
    if (uid) persistHint(uid, documentId);
    /* Toujours un poll immédiat, pas seulement à l'ajout : un Réessayer
       (même documentId, nouveau job côté serveur) doit connaître le nouveau
       statut sans attendre jusqu'à POLL_MS. */
    void pollDocument(documentId);
  }, [uid, persistHint, pollDocument]);

  const releaseJob = useCallback((documentId: string) => {
    setEntries((prev) => {
      const entry = prev.get(documentId);
      if (!entry) return prev;
      const next = new Map(prev);
      if (entry.job.status === 'running') {
        next.set(documentId, { ...entry, claimed: false });
      } else {
        next.delete(documentId);
        if (uid) persistHint(uid, null);
      }
      return next;
    });
  }, [uid, persistHint]);

  const getJob = useCallback(
    (documentId: string) => entriesRef.current.get(documentId)?.job,
    [],
  );

  const dismissNotice = useCallback(() => setNotice(null), []);

  const [sessionCreatedTick, setSessionCreatedTick] = useState(0);
  const notifySessionCreated = useCallback(() => setSessionCreatedTick((t) => t + 1), []);

  const jobs = useMemo(() => [...entries.values()].map((e) => e.job), [entries]);

  const value = useMemo<ExamImportJobsState>(
    () => ({ jobs, notice, dismissNotice, trackJob, releaseJob, getJob, sessionCreatedTick, notifySessionCreated }),
    [jobs, notice, dismissNotice, trackJob, releaseJob, getJob, sessionCreatedTick, notifySessionCreated],
  );

  return <ExamImportJobsContext.Provider value={value}>{children}</ExamImportJobsContext.Provider>;
}

export function useExamImportJobs(): ExamImportJobsState {
  const ctx = useContext(ExamImportJobsContext);
  if (!ctx) throw new Error('useExamImportJobs must be used within ExamImportJobsProvider');
  return ctx;
}

/** Sélecteur réactif — le job suivi pour un document donné, s'il existe. */
export function useTrackedExamImportJob(documentId: string | null): TrackedExamImportJob | undefined {
  const { jobs } = useExamImportJobs();
  return documentId ? jobs.find((j) => j.documentId === documentId) : undefined;
}
